import { type GraphQLError } from 'graphql';

import { type ApolloErrorInterop } from './ApolloErrorInterop';

export type FetchPolicy =
	| 'cache-first'
	| 'cache-and-network'
	| 'network-only'
	| 'no-cache'
	| 'store-or-network';

export type ErrorPolicy = 'none' | 'all';

export type ApolloCompatibleQueryArgs<TData> = {
	fetchData: () => Promise<TData>;
	fetchPolicy?: FetchPolicy;
	errorPolicy?: ErrorPolicy;
	skip?: boolean;
	onCompleted?: (data: TData) => void;
	onError?: (error: ApolloErrorInterop) => void;
	// Optional cache read and write functions for external cache systems (e.g., sweet-state)
	getCachedData?: () => TData | undefined;
	setCachedData?: (data: TData) => void | Promise<void>;
};

export type ApolloCompatibleQueryResult<TData> = {
	data: TData;
	errors: readonly GraphQLError[];
};

export async function apolloCompatibleQuery<TData>({
	fetchData,
	fetchPolicy,
	errorPolicy = 'none',
	skip = false,
	onCompleted,
	onError,
	getCachedData,
	setCachedData,
}: ApolloCompatibleQueryArgs<TData>): Promise<ApolloCompatibleQueryResult<TData>> {
	if (skip) {
		return { data: undefined as TData, errors: [] };
	}

	// cache-first / store-or-network: return cached data if it exists or fetch from network
	if ((fetchPolicy === 'cache-first' || fetchPolicy === 'store-or-network') && getCachedData) {
		const cachedData = getCachedData();
		if (cachedData !== undefined) {
			onCompleted?.(cachedData);
			return { data: cachedData, errors: [] };
		}
	}

	// cache-and-network: return cached data immediately, fetch in background
	if (fetchPolicy === 'cache-and-network' && getCachedData) {
		const cachedData = getCachedData();
		if (cachedData !== undefined) {
			fetchData().catch(() => {});
			onCompleted?.(cachedData);
			return { data: cachedData, errors: [] };
		}
	}

	// network-only or cache miss: fetch from network
	try {
		const data = await fetchData();

		if (fetchPolicy !== 'no-cache' && setCachedData) {
			await setCachedData(data);
		}

		onCompleted?.(data);
		return { data, errors: [] };
	} catch (error) {
		const apolloError = error as ApolloErrorInterop;

		onError?.(apolloError);

		if (errorPolicy === 'all') {
			return {
				data: undefined as TData,
				errors: apolloError.graphQLErrors || [],
			};
		}

		// errorPolicy === 'none': throw the error
		throw apolloError;
	}
}
